// Hi-Lo runner. 4-ball team game settled per-hole.
//
// Each hole produces two points:
//   • LOW point — to the team whose lower-ball score is lower
//     (i.e. compare min(teamA.scores) vs min(teamB.scores)).
//   • HIGH point — to the team whose higher-ball score is lower
//     (i.e. compare max(teamA.scores) vs max(teamB.scores)).
//
// Ties split the point evenly (0.5 to each team). Both team
// members must have a score on the hole for it to settle; otherwise
// the hole is skipped.
//
// Cash settlement is bet-per-point. Each point a team earns delivers
// `pointBet` to each of its two members; each point a team gives up
// costs each of its two members `pointBet`. So a player's per-hole
// net = (myTeam.points − theirTeam.points) × pointBet, which makes
// the round zero-sum across the four players.
//
// Same shape pattern as the other runners — pure function, no
// React/state, returns per-player payouts plus a teamPoints summary
// the leaderboard can display per team.

import { Course, Hole, Player, ScoreMap } from "../types";
import { HandicapMode, adjustHandicaps } from "../handicapAdjust";

// WHS-style course handicap — duplicated from other runners so this
// file stays standalone.
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

export type TeamSetup = "1-2v3-4" | "1-3v2-4" | "1-4v2-3";

export type HiLoScoringMode = "net" | "gross";

export interface HiLoOptions {
  scoringMode?: HiLoScoringMode;
  teamSetup?: TeamSetup;
  betPerPoint?: number;
  // Bet-per-player pot (Bet per Player toggle). Each player antes
  // overallBet; the winning team's two members each take a half-pot,
  // the losing team's each lose their stake. Ignored when
  // useBetPerPoint is true.
  overallBet?: number;
  // Bet-mode picker. true → per-point head-to-head transfers
  // (existing behaviour); false → bet-per-player pot. Default true to
  // keep legacy rounds (saved before the toggle existed) on the
  // per-point math.
  useBetPerPoint?: boolean;
  // Per-game handicap policy. "playOffLow" anchors the lowest player
  // to 0 strokes before WHS conversion. Only affects net Hi-Lo; gross
  // Hi-Lo ignores handicaps entirely.
  handicapMode?: HandicapMode;
  // Auto-press: open a new independent press bet whenever the
  // cumulative main-match score sits at exactly ±2 points after a
  // hole settles. The check runs straight off the live main-match
  // diff every hole — no "since last press" anchor. Each press is
  // its own sub-bet covering only the remaining holes (start hole =
  // trigger hole + 1) and the original match continues unchanged.
  autoPress?: boolean;
}

// Decode the teamSetup token into the two two-player teams. Mirrors
// the helper in strokePlay.ts (kept local so the runner has zero
// cross-runner imports).
function teamsFromSetup<T>(players: T[], setup?: TeamSetup): [T[], T[]] {
  const [a, b, c, d] = players;
  if (setup === "1-3v2-4") return [[a, c], [b, d]];
  if (setup === "1-4v2-3") return [[a, d], [b, c]];
  return [[a, b], [c, d]];
}

export interface HiLoPayout {
  // Per-team point totals are denormalised onto the player record so
  // the leaderboard can render LOW / HIGH columns without re-doing
  // the team grouping. Both teammates carry identical numbers.
  lowPoints: number;
  highPoints: number;
  // Convenience aggregate of LOW + HIGH for the player's team. Used
  // by the trophy / sorting logic.
  teamPoints: number;
  // Net cash position (positive = up). Identical between teammates.
  total: number;
}

// One auto-press bet — independent sub-game that runs from `startHole`
// to the end of the round at the same pointBet as the main wager.
export interface HiLoPress {
  // Sequential index, 1-based, in trigger order.
  index: number;
  // 1-based hole on which the trigger fired (last hole counted toward
  // the trigger comparison).
  triggerHole: number;
  // 1-based first hole this press accumulates points from.
  startHole: number;
  // Per-team point totals across this press's window of holes.
  teamAPoints: number;
  teamBPoints: number;
  // True once THIS press has reached |score| === 2 and spawned its
  // own child press. Each game (main + each press) can trigger only
  // one child in its lifetime — otherwise a press that lingers at
  // ±2 would explode into infinite further presses.
  hasTriggered: boolean;
  // Net cash that flows to each team A / team B member from this press.
  // (a player's contribution to their total is whichever side they're on).
  netPerTeamAPlayer: number;
  netPerTeamBPlayer: number;
}

export interface HiLoResult {
  payouts: Record<string, HiLoPayout>;
  // True when the bet is > 0 AND the round has exactly four players
  // (the only configuration where the runner produces useful output).
  hasAnyBet: boolean;
  // The team-to-team point totals from the round. Exposed so the
  // leaderboard can show a "Team A 12.5 — 5.5 Team B" header if it
  // wants to; the per-player payouts already encode the same data.
  teamPoints: { teamA: number; teamB: number };
  // Auto-press state for the round. `presses` is the ordered list of
  // presses that fired (empty when autoPress is off or never triggered);
  // `autoPressActive` mirrors the option for the panel header.
  presses: HiLoPress[];
  autoPressActive: boolean;
}

export interface HiLoInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: HiLoOptions;
}

function emptyPayout(): HiLoPayout {
  return { lowPoints: 0, highPoints: 0, teamPoints: 0, total: 0 };
}

export function runHiLo(input: HiLoInput): HiLoResult {
  const { course, players, scores, options } = input;
  const scoringMode: HiLoScoringMode =
    options.scoringMode === "gross" ? "gross" : "net";
  // Bet-mode picker — defaults to per-point so legacy rounds saved
  // before the toggle existed keep their head-to-head behaviour.
  const useBetPerPoint = options.useBetPerPoint !== false;
  const pointBet = useBetPerPoint ? Number(options.betPerPoint) || 0 : 0;
  const playerBet = !useBetPerPoint ? Number(options.overallBet) || 0 : 0;
  const autoPress = !!options.autoPress;

  const payouts: Record<string, HiLoPayout> = {};
  for (const p of players) payouts[p.id] = emptyPayout();

  // Defensive — Hi-Lo only makes sense with exactly 4 players. The
  // catalog gate keeps it out of other counts, but a stored round
  // could theoretically reach the runner with the wrong shape.
  if (players.length !== 4) {
    return {
      payouts,
      hasAnyBet: false,
      teamPoints: { teamA: 0, teamB: 0 },
      presses: [],
      autoPressActive: false,
    };
  }

  const [teamA, teamB] = teamsFromSetup(players, options.teamSetup);

  // Apply Hi-Lo's own handicap policy before deriving course
  // handicaps. Identity in "full" mode; lowest-anchored in
  // "playOffLow". Doesn't mutate the stored Player.handicap.
  const adjustedPlayers = adjustHandicaps(players, options.handicapMode);

  // Course handicap cache for net scoring.
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const courseHcpById: Record<string, number> = {};
  for (const p of adjustedPlayers) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const scoreFor = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    if (scoringMode === "gross") return sc.gross;
    return sc.gross - strokesOnHole(courseHcpById[playerId], hole);
  };

  // One award unit = 1 point per hole, split on tie. Ties don't carry
  // forward — the catalog no longer exposes a split-vs-carry toggle,
  // so the behaviour is fixed at "split on tie".
  const settlePoint = (
    aScore: number,
    bScore: number,
  ): { a: number; b: number } => {
    if (aScore < bScore) return { a: 1, b: 0 };
    if (bScore < aScore) return { a: 0, b: 1 };
    return { a: 0.5, b: 0.5 };
  };

  // Main wager running totals.
  let teamALow = 0;
  let teamAHigh = 0;
  let teamBLow = 0;
  let teamBHigh = 0;

  // Auto-press tracking. Each "game" — the main 18-hole match plus
  // every press already born — carries its own running A/B point
  // total. After each settled hole, ANY game whose current diff
  // |A − B| === 2 spawns a new press starting on the next hole, and
  // is then marked `hasTriggered` so it can't spawn again. Without
  // that one-trigger-per-game cap, a press that sits at ±2 for
  // several holes would fire repeatedly and the press list would
  // explode.
  const presses: HiLoPress[] = [];
  let mainHasTriggered = false;

  for (const hole of course.holes) {
    const aScores: number[] = [];
    const bScores: number[] = [];
    let allScored = true;
    for (const p of teamA) {
      const s = scoreFor(p.id, hole);
      if (s == null) {
        allScored = false;
        break;
      }
      aScores.push(s);
    }
    if (!allScored) continue;
    for (const p of teamB) {
      const s = scoreFor(p.id, hole);
      if (s == null) {
        allScored = false;
        break;
      }
      bScores.push(s);
    }
    if (!allScored) continue;

    const aLow = Math.min(...aScores);
    const aHigh = Math.max(...aScores);
    const bLow = Math.min(...bScores);
    const bHigh = Math.max(...bScores);

    const low = settlePoint(aLow, bLow);
    const high = settlePoint(aHigh, bHigh);

    // Tally onto the main wager.
    teamALow += low.a;
    teamBLow += low.b;
    teamAHigh += high.a;
    teamBHigh += high.b;

    // Tally onto every active press (any press whose startHole is <=
    // the current hole). Each press only accumulates from its window
    // onward, so a press triggered on hole 6 starts seeing points on
    // hole 7's settlement.
    for (const pr of presses) {
      if (hole.number < pr.startHole) continue;
      pr.teamAPoints += low.a + high.a;
      pr.teamBPoints += low.b + high.b;
    }

    // Auto-press evaluation — run AFTER the hole's settlement so the
    // current differentials include this hole's contribution. Walk
    // every active game (main + each press born so far) and trigger
    // a fresh press for every game currently sitting at exactly ±2.
    // Skipped on hole 18 since there's nothing left to bet on.
    if (autoPress && pointBet > 0 && hole.number < 18) {
      const newPresses: HiLoPress[] = [];
      const mainDiff =
        teamALow + teamAHigh - (teamBLow + teamBHigh);
      if (!mainHasTriggered && Math.abs(mainDiff) === 2) {
        newPresses.push({
          index: presses.length + newPresses.length + 1,
          triggerHole: hole.number,
          startHole: hole.number + 1,
          teamAPoints: 0,
          teamBPoints: 0,
          hasTriggered: false,
          netPerTeamAPlayer: 0,
          netPerTeamBPlayer: 0,
        });
        mainHasTriggered = true;
      }
      for (const pr of presses) {
        if (pr.hasTriggered) continue;
        // Skip presses that haven't started settling holes yet — they
        // still have a 0/0 score and can't be at ±2.
        if (hole.number < pr.startHole) continue;
        const pressDiff = pr.teamAPoints - pr.teamBPoints;
        if (Math.abs(pressDiff) === 2) {
          newPresses.push({
            index: presses.length + newPresses.length + 1,
            triggerHole: hole.number,
            startHole: hole.number + 1,
            teamAPoints: 0,
            teamBPoints: 0,
            hasTriggered: false,
            netPerTeamAPlayer: 0,
            netPerTeamBPlayer: 0,
          });
          pr.hasTriggered = true;
        }
      }
      for (const np of newPresses) presses.push(np);
    }
  }

  const teamATotal = teamALow + teamAHigh;
  const teamBTotal = teamBLow + teamBHigh;

  // Main wager cash flow:
  //   • Bet per Point — each point a team earns moves pointBet from
  //     every losing-side player to every winning-side player. Net
  //     per player = (myTeamTotal − theirTeamTotal) × pointBet.
  //   • Bet per Player — each of the 4 players antes playerBet (pot
  //     = 4 × playerBet). The team with more total points takes the
  //     pot; each winning-side player gets half the pot (− stake),
  //     each losing-side player loses their stake. Ties refund.
  // playerBet > 0 wins the branch only when useBetPerPoint is false.
  let mainANet = 0;
  let mainBNet = 0;
  if (playerBet > 0) {
    if (teamATotal > teamBTotal) {
      mainANet = playerBet;
      mainBNet = -playerBet;
    } else if (teamBTotal > teamATotal) {
      mainANet = -playerBet;
      mainBNet = playerBet;
    }
  } else if (pointBet > 0) {
    mainANet = (teamATotal - teamBTotal) * pointBet;
    mainBNet = (teamBTotal - teamATotal) * pointBet;
  }

  // Each press settles independently using the same per-point rule
  // but against only the points accrued inside its hole window. Press
  // money is always per-point (presses are a per-point construct);
  // if the round is settling as Bet per Player, presses contribute 0.
  for (const pr of presses) {
    pr.netPerTeamAPlayer = (pr.teamAPoints - pr.teamBPoints) * pointBet;
    pr.netPerTeamBPlayer = (pr.teamBPoints - pr.teamAPoints) * pointBet;
  }

  const pressANet = presses.reduce((s, pr) => s + pr.netPerTeamAPlayer, 0);
  const pressBNet = presses.reduce((s, pr) => s + pr.netPerTeamBPlayer, 0);

  for (const p of teamA) {
    const pay = payouts[p.id];
    pay.lowPoints = teamALow;
    pay.highPoints = teamAHigh;
    pay.teamPoints = teamATotal;
    pay.total = mainANet + pressANet;
  }
  for (const p of teamB) {
    const pay = payouts[p.id];
    pay.lowPoints = teamBLow;
    pay.highPoints = teamBHigh;
    pay.teamPoints = teamBTotal;
    pay.total = mainBNet + pressBNet;
  }

  return {
    payouts,
    hasAnyBet: pointBet > 0 || playerBet > 0,
    teamPoints: { teamA: teamATotal, teamB: teamBTotal },
    presses,
    autoPressActive: autoPress,
  };
}
